import type { ReferenceDataField } from '@moment/trading-types/instrument-data'
import type {
  CellClassParams,
  GetQuickFilterTextParams,
  ICellRendererParams,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community'
import type React from 'react'
import type { ColumnDefinition } from '@/components/atomic/table/types'
import type { DecoratedAggregatedOrderView } from '@/types/api/aggregated/order'
import type { AggregatedRfqView } from '@/types/api/aggregated/rfq'
import type { RFQStatus } from '@/types/api/rfq'
import type { UserBase } from '@/types/api/roles'
import type { MPID } from '@/types/enums/venues'

import { capitalize } from 'lodash'

import Icon, { IconName, PhosphorIconName } from '@/components/atomic/icon'
import ProgressBar from '@/components/atomic/progress-bar'
import { QUANTITY_COLUMN } from '@/components/atomic/table/columns'
import { BodySubtext } from '@/components/atomic/text'
import Tooltip from '@/components/atomic/tooltip'
import { AllocationStatusBadge } from '@/components/molecules/badges/allocation-status'
import { OrderSide as OrderSideComponent } from '@/components/molecules/badges/order-side'
import {
  formatStatus,
  OrderStatusBadge,
} from '@/components/molecules/badges/order-status'
import RfqTimer from '@/components/molecules/rfq-timer'
import getCustodianName from '@/components/trade-desk/utils/get-custodian-name'
import getHumanReadableTraderIdentifier from '@/components/trade-desk/utils/get-human-readable-trader-identifier'
import { getHumanReadableUserIdentifierById } from '@/components/trade-desk/utils/get-human-readable-user-identifier-by-id'
import {
  formatDiscretionaryAuthority,
  resolveOrderDiscretionaryAuthority,
} from '@/components/trade-desk/utils/resolve-discretionary-authority'
import { getGainLossClass } from '@/portfolio/account-details/helpers/gain-loss'
import {
  getIdentifierPreference,
  IdentifierPreference,
} from '@/shared/helpers/identifier-preference'
import {
  getOrderPrimeBrokerageStatus,
  renderPrimeBrokerageIconForAccount,
  renderPrimeBrokerageIconForBlockTrade,
} from '@/trading/helpers/prime-brokerage-status'
import { getVenueLabel } from '@/trading/helpers/venues'
import { UNASSIGNED_TRADER_ID } from '@/trading/hooks/use-get-trader-options'
import {
  type LPLPlatform,
  LPLPlatformLabels,
} from '@/trading/trade-desk-blotter/bulk-allocation-upload/types'
import {
  type OrderExecutionEvent,
  type OrderUpdateEvent,
  OrderUpdateEventType,
} from '@/types/api/events/order-update'
import {
  type TradingEventMessage,
  TradingEventMessageType,
} from '@/types/api/events/trading-event'
import {
  type ExecutionOrder,
  ExecutionOrderProtocol,
  ExecutionOrderStatus,
} from '@/types/api/order'
import { ColumnId } from '@/types/enums/column-id'
import {
  OrderMetadataFields,
  OrderSizeType,
  OrderType,
} from '@/types/enums/order'
import { TradeBlotterColumnId } from '@/types/enums/trade-blotter'
import { TradeDeskTradeBlotterColumnId } from '@/types/enums/trade-desk-trade-blotter'
import { formatDate, parseISOString } from '@/utils/dates/dateFormatting'
import { formatIntegerToUSDate } from '@/utils/dates/dateHelpers'
import {
  type AssetClass,
  getAssetClassFromOrder,
  isFixedIncome,
  isFixedIncomeAmount,
  isMutualFundAmount,
  toOrderSizeType,
} from '@/utils/instrument-classification'
import { formatYield } from '@/utils/presentation/bonds'
import { getDescriptionLabel } from '@/utils/presentation/description'
import {
  defaultValueTransform,
  formatMoney,
  formatPercentage,
  isNullish,
  PLACEHOLDER,
} from '@/utils/presentation/formatting'
import { getNotionalOrderValue } from '@/utils/presentation/notional-value'
import { getPriceDisplay, MARKET_ORDER_LABEL } from '@/utils/presentation/price'
import {
  getQuantityDisplay,
  getQuantitySortValue,
} from '@/utils/presentation/quantity'
import {
  isRfqInTerminalState,
  isTerminalStatus,
} from '@/utils/trade-blotter/compute-order-status'

export function getAccountColumnDisplayText(
  order: DecoratedAggregatedOrderView | null,
): string {
  if (!order) {
    return PLACEHOLDER
  }
  if (order.is_block_trade) {
    const accountRequests = order.account_requests ?? []
    // The same account can appear in multiple allocation requests; count it once
    const uniqueAccountIds = new Set(
      accountRequests.map((request) => request.account_id),
    )
    const numAccounts = uniqueAccountIds.size
    if (numAccounts === 0) {
      return PLACEHOLDER
    }
    // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
    const firstAccountId = accountRequests[0]!.account_id

    // NOTE: CSV export FULLY RELIES on the assumption that multi-allocation orders are marked with "N accounts"
    // This function returns raw account IDs for single accounts, which the CSV export then expands using order data.
    // CSV export is defined in processOrdersCsvToAllocationCsv in trading/trade-desk-blotter/helpers/export/export-orders-as-allocations.tsx
    return numAccounts === 1 ? firstAccountId : `${numAccounts} accounts`
  }
  const accountId = order.account_id ?? ''
  return accountId
}

export enum TradeBlotterHeaders {
  TIME = 'Time',
  CUSIP = 'CUSIP',
  REJECT_REASON = 'Reject reason',
  CANCEL_REASON = 'Cancel reason',
  MOMENT_ORDER_ID = 'Moment Order ID',
  CLIENT_ORDER_ID = 'Client Order ID',
  EXTENDED_HOURS = 'Extended hours',
  DESCRIPTION = 'Description',
  DESCRIPTION_SHORT = 'Security',
  ORDER_STATUS = 'Order status',
  ALLOCATION_STATUS = 'Allocation status',
  EXECUTION = 'Execution',
  SIDE = 'Side',
  GROUP = 'Group',
  ACCOUNT = 'Account',
  CUSTODIAN = 'Custodian',
  PRIME_BROKERAGE = 'Prime brokerage',
  LIMIT_PRICE = 'Limit price',
  TIME_LEFT = 'Time left',
  TOTAL_QTY = 'Total qty',
  FILLED_QTY = 'Filled qty',
  ROUTED_QTY = 'Routed qty',
  WORKING_QTY = 'Working qty',
  UNROUTED_QTY = 'Unrouted qty',
  QUANTITY_TYPE = 'Quantity type',
  FILLED_AVG_PRICE = 'Filled avg price',
  VENUES = 'Venue(s)',
  COUNTERPARTIES = 'Counterparty(s)',
  ORGANIZATION = 'Organization',
  ANNOTATION = 'Annotation',
  FILL_PROGRESS = 'Fill progress',
  TRADER = 'Trader',
  SUBFIRM = 'Subfirm',
  PLATFORM = 'Platform',
  DISCRETIONARY_AUTHORITY = 'Discretionary authority',
  IAR = 'IAR',
  IAR_EMAIL = 'IAR email',
  SUBMISSION_NAME = 'Submission Name',
  HANDLING_STATUS = 'Handling status',
  LIST_RFQ_NAME = 'List RFQ Name',
  ACCRUED_INTEREST = 'Accrued interest',
  SETTLEMENT_DATE = 'Settlement date',
  NET_MONEY = 'Net money',
  PRE_FEE_NET_MONEY = 'Pre-fee net money',
  NOTIONAL_VALUE = 'Notional value',
  LAST_PRICE = 'Last price',
  LAST_PRICE_ASOF = 'Last price as of',
  PERCENT_CHANGE_1D = '%1D change',
  DAY_RANGE = 'Range',
  BID_PRICE = 'Bid',
  BID_PRICE_ASOF = 'Bid as of',
  ASK_PRICE = 'Ask',
  ASK_PRICE_ASOF = 'Ask as of',

  // Export-only columns injected by processOrdersCsvToAllocationCsv.
  // They have no grid column, so keep their labels distinct from DESCRIPTION_SHORT
  // and ACCOUNT to avoid colliding with grid-exported headers.
  SECURITY_NAME = 'Security name',
  ACCOUNT_NAME = 'Account name',
}

interface ColumnCreationProps {
  cellRenderer?: string
  colId?: string
  field?: ReferenceDataField
  filter?: string
  headerClass?: string
  headerName?: string
  hide?: boolean
  minWidth?: number
  rowGroup?: boolean
  sort?: 'asc' | 'desc'
  meta?: {
    onClickApprove?: (id: string) => void
    onClickReject?: (id: string) => void
    // Trader column: render initials instead of the full trader name. Gated by
    // the `trading_launch.showTraderInitialsOnBlotter` config, independent of
    // the Equities UI rollout.
    showTraderInitials?: boolean
    [key: string]: unknown
  }
}

/**
 * A blotter column definition that may carry a `proposedCellRenderer` applied
 * when the Equities UI flag is on. Kept local to the blotter rather than on the
 * shared `ColumnDefinition`, which must stay assignable to AG Grid's `ColDef`.
 */
export type TradeBlotterColumnDefinition = ColumnDefinition & {
  proposedCellRenderer?: ColumnDefinition['cellRenderer']
  // Per-column resize floor under the Equities UI rollout; falls back to
  // ROLLOUT_MIN_WIDTH when unset. Stripped before the def reaches AG Grid.
  rolloutMinWidth?: number
}

interface ColumnCreator {
  create: (props: ColumnCreationProps) => TradeBlotterColumnDefinition
}

// Default resize floor under the rollout; a few dense columns opt lower via
// each column's `rolloutMinWidth`.
const ROLLOUT_MIN_WIDTH = 50

/**
 * Sizing applied to every column under the Equities UI rollout: resizable with
 * no max-width cap, and `flex` disabled so each column's own `width` becomes its
 * start width. (The grid defaults columns to `flex: 1`, which ignores `width`;
 * disabling flex here lets the per-column `width` take effect.) The minWidth
 * floor is applied per-column in `resolveColumnLayouts`.
 */
const ROLLOUT_COLUMN_LAYOUT: Partial<ColumnDefinition> = {
  flex: 0,
  maxWidth: undefined,
  resizable: true,
}

/**
 * Under the Equities UI rollout every column becomes resizable down to its
 * `rolloutMinWidth` (default `ROLLOUT_MIN_WIDTH`) and starts at its own inline
 * `width`; with the flag off, columns keep their original sizing (flex stays on,
 * so the inline `width` is ignored). The local `rolloutMinWidth` field is
 * stripped either way so it never reaches AG Grid; `proposedCellRenderer` is
 * left intact for the renderer pass.
 */
export function resolveColumnLayouts(
  columns: TradeBlotterColumnDefinition[],
  useProposedLayout: boolean,
): TradeBlotterColumnDefinition[] {
  return columns.map(({ rolloutMinWidth, ...column }) => {
    if (!useProposedLayout) {
      return column
    }
    return {
      ...column,
      ...ROLLOUT_COLUMN_LAYOUT,
      minWidth: rolloutMinWidth ?? ROLLOUT_MIN_WIDTH,
    }
  })
}

/**
 * Swaps in each column's `proposedCellRenderer` when `useProposedLayout` is true
 * (the Equities UI rollout) and always strips the field so it never reaches AG
 * Grid. Columns without one are returned unchanged.
 */
export function resolveProposedCellRenderers(
  columns: TradeBlotterColumnDefinition[],
  useProposedLayout: boolean,
): ColumnDefinition[] {
  return columns.map(({ proposedCellRenderer, ...column }) =>
    useProposedLayout && proposedCellRenderer
      ? { ...column, cellRenderer: proposedCellRenderer }
      : column,
  )
}

export const TIME: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? TradeBlotterHeaders.TIME,
      field: 'created_at',
      sort: props.sort,
      valueFormatter: (params: ValueFormatterParams): string => {
        if (isNullish(params.value)) {
          return PLACEHOLDER
        }
        return formatDate(parseISOString(params.value), 'MM/dd/yyyy hh:mm:ss a')
      },
      minWidth: 175,
      maxWidth: 175,
      resizable: false,
      hide: props.hide,
      colId: TradeBlotterColumnId.TIME,
      width: 154,
      rowGroup: props.rowGroup,
    } satisfies ColumnDefinition
  },
}

export const ORDER_CUSIP: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      field: 'cusip',
      headerName: TradeBlotterHeaders.CUSIP,
      colId: ColumnId.CUSIP,
      width: 83,
      minWidth: 100,
      maxWidth: 100,
      resizable: false,
      filter: 'agSetColumnFilter',
      cellRenderer: (params: ICellRendererParams) => {
        return defaultValueTransform(params.value)
      },
      ...props,
    } satisfies ColumnDefinition
  },
}

export const ORDER_IDENTIFIER: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      field: 'identifier',
      headerValueGetter: () => {
        const preference = getIdentifierPreference()
        return preference === IdentifierPreference.ISIN ? 'ISIN' : 'CUSIP'
      },
      colId: ColumnId.IDENTIFIER,
      width: 100,
      minWidth: 125,
      maxWidth: 125,
      resizable: false,
      filter: 'agSetColumnFilter',
      valueGetter: (params: ValueGetterParams) => {
        const preference = getIdentifierPreference()
        const value =
          preference === IdentifierPreference.ISIN
            ? params.data?.isin
            : params.data?.cusip
        return value
      },
      cellRenderer: (params: ICellRendererParams) => {
        return defaultValueTransform(params.value)
      },
      ...props,
    } satisfies ColumnDefinition
  },
}

export const ORDER_REJECT_REASON: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      field: 'reject_reason',
      headerName: TradeBlotterHeaders.REJECT_REASON,
      colId: TradeDeskTradeBlotterColumnId.REJECT_REASON,
      minWidth: 150,
      width: 100,
      filter: 'agSetColumnFilter',
      ...props,
    } satisfies ColumnDefinition
  },
}

export const ORDER_CANCEL_REASON: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      field: 'cancel_reason',
      headerName: TradeBlotterHeaders.CANCEL_REASON,
      colId: TradeDeskTradeBlotterColumnId.CANCEL_REASON,
      minWidth: 150,
      width: 100,
      filter: 'agSetColumnFilter',
      ...props,
    } satisfies ColumnDefinition
  },
}

export const ORDER_ID: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      field: 'id',
      headerName: TradeBlotterHeaders.MOMENT_ORDER_ID,
      colId: TradeDeskTradeBlotterColumnId.ID,
      minWidth: 250,
      width: 100,
      filter: 'agSetColumnFilter',
      cellRenderer: (params: ICellRendererParams) => {
        return defaultValueTransform(params.value)
      },
      ...props,
    } satisfies ColumnDefinition
  },
}

export const CLIENT_ORDER_ID: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      field: 'client_order_id',
      headerName: TradeBlotterHeaders.CLIENT_ORDER_ID,
      colId: TradeDeskTradeBlotterColumnId.CLIENT_ORDER_ID,
      minWidth: 250,
      width: 100,
      filter: 'agSetColumnFilter',
      cellRenderer: (params: ICellRendererParams) => {
        return defaultValueTransform(params.value)
      },
      ...props,
    } satisfies ColumnDefinition
  },
}

export const ORDER_EXTENDED_HOURS: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    const getCellValue = (
      params:
        | ICellRendererParams
        | GetQuickFilterTextParams
        | ValueGetterParams,
    ): string => {
      if (params.data.extended_hours) {
        return 'Yes'
      } else {
        return 'No'
      }
    }
    return {
      field: 'extended_hours',
      headerName: TradeBlotterHeaders.EXTENDED_HOURS,
      colId: TradeDeskTradeBlotterColumnId.EXTENDED_HOURS,
      width: 100,
      minWidth: 120,
      maxWidth: 120,
      cellRenderer: (params: ICellRendererParams) => {
        return getCellValue(params)
      },
      filterValueGetter: (params: ValueGetterParams): string => {
        return getCellValue(params)
      },
      getQuickFilterText: (params: GetQuickFilterTextParams): string => {
        return getCellValue(params)
      },
      filter: 'agSetColumnFilter',
      ...props,
    } satisfies ColumnDefinition
  },
}

export const ORDER_DESCRIPTION: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    const getCellValue = (
      params:
        | ICellRendererParams
        | GetQuickFilterTextParams
        | ValueGetterParams,
    ): string | null => {
      return getDescriptionLabel({
        assetType: params.data?.assetType ?? null,
        symbol: params.data?.symbol ?? null,
        fallback: params.data?.description ?? null,
      })
    }
    return {
      headerName: props.headerName ?? TradeBlotterHeaders.DESCRIPTION,
      valueGetter: (params: ValueGetterParams): string | null => {
        return getCellValue(params)
      },
      valueFormatter: (params: ValueFormatterParams): string => {
        if (isNullish(params.value)) {
          return PLACEHOLDER
        }
        return params.value
      },
      getQuickFilterText: (params: GetQuickFilterTextParams): string => {
        return getCellValue(params) ?? ''
      },
      minWidth: 300,
      hide: props.hide,
      colId: TradeBlotterColumnId.DESCRIPTION,
      width: 100,
      filter: 'agSetColumnFilter',
    } satisfies ColumnDefinition
  },
}

export const ORDER_DESCRIPTION_SHORT: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    const getCellValue = (
      params:
        | ICellRendererParams
        | GetQuickFilterTextParams
        | ValueGetterParams,
    ): string | null => {
      return getDescriptionLabel({
        assetType: params.data?.assetType ?? null,
        symbol: params.data?.symbol ?? null,
        fallback: params.data?.description_short ?? null,
      })
    }
    return {
      headerName: props.headerName ?? TradeBlotterHeaders.DESCRIPTION_SHORT,
      valueGetter: (params: ValueGetterParams): string | null => {
        return getCellValue(params)
      },
      valueFormatter: (params: ValueFormatterParams): string => {
        if (isNullish(params.value)) {
          return PLACEHOLDER
        }
        return params.value
      },
      getQuickFilterText: (params: GetQuickFilterTextParams): string => {
        return getCellValue(params) ?? ''
      },
      minWidth: 175,
      hide: props.hide,
      colId: TradeBlotterColumnId.DESCRIPTION_SHORT,
      width: 71,
      filter: 'agSetColumnFilter',
    } satisfies ColumnDefinition
  },
}

export const ADVISOR_VIEW_ORDER_STATUS: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? TradeBlotterHeaders.ORDER_STATUS,
      field: 'advisorViewStatus',
      cellRenderer: (params: ICellRendererParams) => {
        if (isNullish(params.value)) {
          return PLACEHOLDER
        }
        return <OrderStatusBadge status={params.value} />
      },
      filterValueGetter: (params: ValueGetterParams): string => {
        return formatStatus(params.data.advisorViewStatus)
      },
      minWidth: 150,
      hide: props.hide,
      colId: TradeBlotterColumnId.STATUS,
      width: 100,
      filter: 'agSetColumnFilter',
    } satisfies ColumnDefinition
  },
}

export const TRADER_VIEW_ORDER_STATUS: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? TradeBlotterHeaders.ORDER_STATUS,
      field: 'traderViewStatus',
      cellRenderer: (params: ICellRendererParams) => {
        if (isNullish(params.value)) {
          return PLACEHOLDER
        }
        return <OrderStatusBadge status={params.value} />
      },
      filterValueGetter: (params: ValueGetterParams): string => {
        return formatStatus(params.data.traderViewStatus)
      },
      minWidth: 125,
      hide: props.hide,
      colId: TradeDeskTradeBlotterColumnId.STATUS,
      width: 126,
      filter: 'agSetColumnFilter',
    } satisfies ColumnDefinition
  },
}

export const ORDER_ALLOCATION_STATUS: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? TradeBlotterHeaders.ALLOCATION_STATUS,
      field: 'allocation_status',
      cellRenderer: (params: ICellRendererParams) => {
        if (!params.data) {
          return PLACEHOLDER
        }

        // For non-block trades, there is no allocation
        if (!params.data.is_block_trade) {
          return PLACEHOLDER
        }

        if (isNullish(params.value)) {
          return PLACEHOLDER
        }
        return <AllocationStatusBadge status={params.value} />
      },
      filterValueGetter: (params: ValueGetterParams): string => {
        return capitalize(params.data.allocation_status)
      },
      minWidth: 110,
      hide: props.hide,
      colId: TradeDeskTradeBlotterColumnId.ALLOCATION_STATUS,
      width: 94,
      filter: 'agSetColumnFilter',
    } satisfies ColumnDefinition
  },
}

const getSuccessfulExecutionOrders = (
  executionOrders: ExecutionOrder[],
): ExecutionOrder[] => {
  return executionOrders.filter(
    (order) =>
      order.status === ExecutionOrderStatus.Filled ||
      order.status === ExecutionOrderStatus.PartiallyFilled,
  )
}

export const ORDER_EXECUTION: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    const getCellValue = (
      params:
        | ICellRendererParams
        | GetQuickFilterTextParams
        | ValueGetterParams,
      options?: { convertToLabel?: boolean },
    ): string => {
      const allExecutionOrders = params.data
        .execution_orders as ExecutionOrder[]
      if (!allExecutionOrders || allExecutionOrders.length === 0) {
        if (isTerminalStatus(params.data.traderViewStatus)) {
          return PLACEHOLDER
        }
        return 'Pending...'
      }

      const successfulExecutionOrders =
        getSuccessfulExecutionOrders(allExecutionOrders)
      if (successfulExecutionOrders.length === 0) {
        if (isTerminalStatus(params.data.traderViewStatus)) {
          return PLACEHOLDER
        }
        return 'Pending...'
      }

      const uniqueProtocols = new Set(
        successfulExecutionOrders.map((order) => order.protocol),
      )
      if (uniqueProtocols.size > 1) {
        return 'Multiple'
      }

      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      const protocol = successfulExecutionOrders[0]!.protocol
      if (options && !options.convertToLabel) {
        return protocol
      }
      switch (protocol) {
        case ExecutionOrderProtocol.LOB:
          return 'LOB'
        case ExecutionOrderProtocol.RFQ:
          return 'RFQ'
        case ExecutionOrderProtocol.External:
          return 'Voice trade'
        case ExecutionOrderProtocol.Group:
          return 'Group'
        case ExecutionOrderProtocol.Custodian:
          return 'Custodian'
        default:
          return protocol
      }
    }
    return {
      headerName: props.headerName ?? TradeBlotterHeaders.EXECUTION,
      cellRenderer: (params: ICellRendererParams): React.ReactNode => {
        const cellValue = getCellValue(params)
        if (cellValue === 'Pending...' || cellValue === PLACEHOLDER) {
          return (
            <BodySubtext className="text-[#adbaca]">{cellValue}</BodySubtext>
          )
        }
        return <BodySubtext className="text-primary">{cellValue}</BodySubtext>
      },
      getQuickFilterText: (params: GetQuickFilterTextParams): string => {
        return getCellValue(params)
      },
      filterValueGetter: (params: ValueGetterParams): string => {
        return getCellValue(params, { convertToLabel: true })
      },
      valueGetter: (params: ValueGetterParams): string => {
        return getCellValue(params)
      },
      minWidth: 110,
      maxWidth: 110,
      resizable: false,
      hide: props.hide,
      colId: TradeDeskTradeBlotterColumnId.EXECUTION,
      width: 76,
      filter: 'agSetColumnFilter',
    } satisfies ColumnDefinition
  },
}

export const ORDER_SIDE: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? TradeBlotterHeaders.SIDE,
      field: 'side',
      cellRenderer: (params: ICellRendererParams) => {
        if (isNullish(params.value)) {
          return PLACEHOLDER
        }
        return <OrderSideComponent side={params.value} />
      },
      filterValueGetter: (params: ValueGetterParams): string => {
        return capitalize(params.data.side)
      },
      minWidth: 60,
      maxWidth: 60,
      resizable: false,
      hide: props.hide,
      colId: TradeBlotterColumnId.SIDE,
      width: 51,
      rolloutMinWidth: 40,
      filter: 'agSetColumnFilter',
    } satisfies TradeBlotterColumnDefinition
  },
}

export const ORDER_ACCOUNT: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    const getCellValue = (
      params:
        | ICellRendererParams
        | GetQuickFilterTextParams
        | ValueGetterParams,
    ): string => {
      return getAccountColumnDisplayText(params.data)
    }
    return {
      headerName: props.headerName ?? TradeBlotterHeaders.ACCOUNT,
      cellRenderer: (params: ICellRendererParams) => {
        return (
          <BodySubtext className="text-primary">
            {getCellValue(params)}
          </BodySubtext>
        )
      },
      valueGetter: (params: ValueGetterParams): string => {
        return getCellValue(params)
      },
      getQuickFilterText: (params: GetQuickFilterTextParams): string => {
        return getCellValue(params)
      },
      filterValueGetter: (params: ValueGetterParams): string => {
        return getCellValue(params)
      },
      minWidth: 125,
      hide: props.hide,
      colId: TradeBlotterColumnId.ACCOUNT,
      width: 85,
      filter: 'agSetColumnFilter',
    } satisfies ColumnDefinition
  },
}

export const ORDER_CUSTODIAN: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    const getCellValue = (
      params:
        | ICellRendererParams
        | GetQuickFilterTextParams
        | ValueGetterParams,
    ): string => {
      return getCustodianName(params.data)
    }
    return {
      headerName: props.headerName ?? TradeBlotterHeaders.CUSTODIAN,
      cellRenderer: (params: ICellRendererParams) => {
        return (
          <BodySubtext className="text-primary">
            {getCellValue(params)}
          </BodySubtext>
        )
      },
      valueGetter: (params: ValueGetterParams): string => {
        return getCellValue(params)
      },
      getQuickFilterText: (params: GetQuickFilterTextParams): string => {
        return getCellValue(params)
      },
      filterValueGetter: (params: ValueGetterParams): string => {
        return getCellValue(params)
      },
      minWidth: 125,
      hide: props.hide,
      colId: TradeDeskTradeBlotterColumnId.CUSTODIAN,
      width: 80,
      filter: 'agSetColumnFilter',
    } satisfies ColumnDefinition
  },
}

export const ORDER_PRIME_BROKERAGE_ENABLED: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    // Resolves to the set-filter / sort bucket. The unknown state shows as the
    // "—" placeholder in the cell but is labelled "N/A" in the filter dropdown.
    const getFilterValue = (
      params:
        | ICellRendererParams
        | GetQuickFilterTextParams
        | ValueGetterParams,
    ): string => {
      if (!params.data) {
        return 'N/A'
      }
      const status = getOrderPrimeBrokerageStatus(params.data)
      if (status === undefined) {
        return 'N/A'
      }
      return status ? 'Yes' : 'No'
    }
    return {
      headerName: props.headerName ?? TradeBlotterHeaders.PRIME_BROKERAGE,
      field: 'account_requests',
      cellRenderer: (params: ICellRendererParams) => {
        if (!params.data) {
          return PLACEHOLDER
        }

        // Handle block trade with multiple accounts
        if (params.data.is_block_trade) {
          return renderPrimeBrokerageIconForBlockTrade(
            params.data.account_requests || [],
          )
        }

        // Handle single account
        return renderPrimeBrokerageIconForAccount(
          params.data.account_id,
          params.data.account_requests,
        )
      },
      // valueGetter drives sorting; the underlying field is an array, so sorting
      // on it directly is meaningless. cellRenderer still reads params.data, so
      // the icons are unaffected.
      valueGetter: getFilterValue,
      filterValueGetter: getFilterValue,
      getQuickFilterText: getFilterValue,
      filter: 'agSetColumnFilter',
      minWidth: 125,
      maxWidth: 125,
      resizable: false,
      hide: props.hide,
      colId: TradeDeskTradeBlotterColumnId.PRIME_BROKERAGE_ENABLED,
      width: 100,
      sortable: true,
    } satisfies ColumnDefinition
  },
}

/**
 * Handling status column.
 * Refers to whether the order was submitted for auto-ex or manual trader intervention.
 */
export const ORDER_HANDLING_STATUS: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? TradeBlotterHeaders.HANDLING_STATUS,
      field: 'execution_orders',
      cellRenderer: (params: ICellRendererParams) => {
        if (!params.data) {
          return PLACEHOLDER
        }

        // The first execution order is the only one that could have been submitted with auto-ex rules
        const field = params.data.execution_orders?.[0]?.ruleset_metadata
        const tooltipText = field
          ? 'Tagged by auto-ex rules'
          : // biome-ignore lint/style/noNestedTernary: Existing code — enable rule without runtime changes
            params.data.bypass_auto_ex
            ? 'Auto-ex bypassed; manual handling required'
            : 'Manual handling required'
        const icon = field
          ? {
              name: PhosphorIconName.ArrowsSplitIcon,
              isPhosphor: true,
              className:
                'text-moment-purple-700 dark:text-moment-purple-400 rotate-270',
            }
          : {
              name: IconName.HandRaisedIcon,
              isPhosphor: false,
              className: 'text-moment-orange-700 dark:text-moment-orange-500',
            }
        return (
          <Tooltip simpleText={tooltipText} side="right">
            {/* This is needed because the icon is an SVG that does not support pointer-events */}
            <div className="pointer-events-auto">
              <Icon
                iconName={icon.name}
                className={icon.className}
                isPhosphor={icon.isPhosphor}
                outline
              />
            </div>
          </Tooltip>
        )
      },
      minWidth: 125,
      maxWidth: 125,
      resizable: false,
      hide: props.hide,
      colId: TradeDeskTradeBlotterColumnId.HANDLING_STATUS,
      width: 50,
      sortable: false,
    } satisfies ColumnDefinition
  },
}

export const ORDER_LIMIT_PRICE: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    const getCellValue = (
      params:
        | ICellRendererParams
        | GetQuickFilterTextParams
        | ValueGetterParams,
    ): string => {
      const amount = params.data?.amount
      const limitPrice = params.data?.limit_price
      // Market orders display as "Market" for every asset class.
      const marketLabel = MARKET_ORDER_LABEL
      // Mutual funds have no per-unit price; show PLACEHOLDER even when
      // an upstream system records a Market order type, to honor the spec.
      if (amount?.type === OrderSizeType.Notional) {
        return PLACEHOLDER
      }
      if (isNullish(limitPrice)) {
        if (params.data?.type === OrderType.Market) {
          return marketLabel
        }
        return PLACEHOLDER
      }
      // Market orders may carry limit_price = 0 instead of null.
      if (limitPrice === 0) {
        return marketLabel
      }
      return getPriceDisplay({
        price: limitPrice,
        amountType: toOrderSizeType(amount?.type),
      })
    }
    return {
      headerName: props.headerName ?? TradeBlotterHeaders.LIMIT_PRICE,
      cellClass: 'tabular-nums flex justify-end',
      valueGetter: (params: ValueGetterParams): string => {
        return getCellValue(params)
      },
      getQuickFilterText: (params: GetQuickFilterTextParams): string => {
        return getCellValue(params)
      },
      filterValueGetter: (params: ValueGetterParams): number => {
        const value = getCellValue(params)
        // Non-price labels ("Market" / placeholder) sort and filter as 0
        // since there's no numeric limit to compare against.
        if (value === MARKET_ORDER_LABEL || value === PLACEHOLDER) {
          return 0
        }
        return params.data.limit_price
      },
      minWidth: 120,
      hide: props.hide,
      colId: TradeBlotterColumnId.LIMIT_PRICE,
      width: 84,
      filter: 'agNumberColumnFilter',
    } satisfies ColumnDefinition
  },
}

export const ORDER_TIME_LEFT: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? TradeBlotterHeaders.TIME_LEFT,
      field: 'rfq_views',
      cellRenderer: (params: ICellRendererParams) => {
        const rfqViews = [...(params.data.rfq_views ?? [])]
        const latestRfq = rfqViews.sort(
          (a, b) =>
            new Date(b.latest_state.created_at ?? '').getTime() -
            new Date(a.latest_state.created_at ?? '').getTime(),
        )[0] as AggregatedRfqView
        if (isNullish(latestRfq)) {
          return (
            <BodySubtext className="text-[#adbaca]">{PLACEHOLDER}</BodySubtext>
          )
        }
        const isOrderFilled = isTerminalStatus(params.data.traderViewStatus)
        const isRfqRejected = isRfqInTerminalState(
          latestRfq.latest_state.status as RFQStatus,
        )
        if (isOrderFilled || isRfqRejected) {
          return (
            <BodySubtext className="text-[#adbaca]">{PLACEHOLDER}</BodySubtext>
          )
        }
        return <RfqTimer rfqSummary={latestRfq.latest_state} />
      },
      minWidth: 120,
      maxWidth: 120,
      resizable: false,
      sortable: false,
      filter: false,
      hide: props.hide,
      colId: TradeBlotterColumnId.TIME_LEFT,
      width: 50,
      rolloutMinWidth: 40,
    } satisfies TradeBlotterColumnDefinition
  },
}

export const ORDER_QUANTITY: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return QUANTITY_COLUMN({
      headerName: props.headerName ?? TradeBlotterHeaders.TOTAL_QTY,
      headerClass: '',
      field: 'amount.value',
      minWidth: 120,
      hide: props.hide,
      colId: TradeBlotterColumnId.QUANTITY,
      width: 98,
      filter: 'agNumberColumnFilter',
      valueGetter: (params: ValueGetterParams): number => {
        return getQuantitySortValue(params.data?.amount)
      },
      valueFormatter: (params: ValueFormatterParams): string => {
        return getQuantityDisplay(params.data?.amount)
      },
    })
  },
}

export const ORDER_NOTIONAL_VALUE: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? TradeBlotterHeaders.NOTIONAL_VALUE,
      cellClass: 'tabular-nums flex justify-end',
      // Computed per order: filled (or terminal) shares use the realized average
      // fill price, unfilled active shares estimate from the last price, notional
      // (MF / dollar equity) echoes the amount, fixed income is blank. See
      // getNotionalOrderValue. The numeric getter drives native sort + filter.
      valueGetter: (params: ValueGetterParams): number | null => {
        const order = params.data
        const status = order?.traderViewStatus
        const isTerminal = status != null && isTerminalStatus(status)
        return getNotionalOrderValue({
          amount: order?.amount,
          filledQty: order?.filled_amount?.value,
          avgFillPrice: order?.filled_avg_price?.pop,
          lastPrice: order?.nbboLastPrice,
          isTerminal,
        })
      },
      valueFormatter: (params: ValueFormatterParams): string => {
        if (isNullish(params.value)) {
          return PLACEHOLDER
        }
        return formatMoney(params.value)
      },
      // minWidth mirrors the NBBO market-data columns this is gated and shown
      // with (the Equities-UI rollout layout floors every column at 50).
      minWidth: 50,
      hide: props.hide,
      colId: TradeDeskTradeBlotterColumnId.NOTIONAL_VALUE,
      width: 110,
      filter: 'agNumberColumnFilter',
    } satisfies ColumnDefinition
  },
}

export const ORDER_FILLED: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return QUANTITY_COLUMN({
      headerName: props.headerName ?? TradeBlotterHeaders.FILLED_QTY,
      headerClass: '',
      field: 'filled_amount.value',
      cellClass: 'tabular-nums flex justify-end',
      minWidth: 120,
      hide: props.hide,
      colId: TradeBlotterColumnId.FILLED,
      width: 74,
      filter: 'agNumberColumnFilter',
      valueGetter: (params: ValueGetterParams): number => {
        return getQuantitySortValue(params.data?.filled_amount)
      },
      valueFormatter: (params: ValueFormatterParams): string => {
        return getQuantityDisplay(params.data?.filled_amount)
      },
    })
  },
}

// Pre-derived quantity breakdown columns (Routed / Working / Unrouted), stamped
// on the row during order decoration (see QuantityBreakdownFields). Each is a
// self-describing `Amount`, so it formats and sorts off its own unit exactly
// like Total / Filled — no manual unit pairing. Routed = Filled + Working;
// Total = Routed + Unrouted.
export const ORDER_ROUTED: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return QUANTITY_COLUMN({
      headerName: props.headerName ?? TradeBlotterHeaders.ROUTED_QTY,
      headerClass: '',
      field: 'routedAmount.value',
      cellClass: 'tabular-nums flex justify-end',
      minWidth: 120,
      hide: props.hide,
      colId: TradeDeskTradeBlotterColumnId.ROUTED,
      width: 74,
      filter: 'agNumberColumnFilter',
      valueGetter: (params: ValueGetterParams): number => {
        return getQuantitySortValue(params.data?.routedAmount)
      },
      valueFormatter: (params: ValueFormatterParams): string => {
        return getQuantityDisplay(params.data?.routedAmount)
      },
    })
  },
}

export const ORDER_WORKING: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return QUANTITY_COLUMN({
      headerName: props.headerName ?? TradeBlotterHeaders.WORKING_QTY,
      headerClass: '',
      field: 'workingAmount.value',
      cellClass: 'tabular-nums flex justify-end',
      minWidth: 120,
      hide: props.hide,
      colId: TradeDeskTradeBlotterColumnId.WORKING,
      width: 74,
      filter: 'agNumberColumnFilter',
      valueGetter: (params: ValueGetterParams): number => {
        return getQuantitySortValue(params.data?.workingAmount)
      },
      valueFormatter: (params: ValueFormatterParams): string => {
        return getQuantityDisplay(params.data?.workingAmount)
      },
    })
  },
}

export const ORDER_UNROUTED: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return QUANTITY_COLUMN({
      headerName: props.headerName ?? TradeBlotterHeaders.UNROUTED_QTY,
      headerClass: '',
      field: 'unroutedAmount.value',
      cellClass: 'tabular-nums flex justify-end',
      minWidth: 120,
      hide: props.hide,
      colId: TradeDeskTradeBlotterColumnId.UNROUTED,
      width: 74,
      filter: 'agNumberColumnFilter',
      valueGetter: (params: ValueGetterParams): number => {
        return getQuantitySortValue(params.data?.unroutedAmount)
      },
      valueFormatter: (params: ValueFormatterParams): string => {
        return getQuantityDisplay(params.data?.unroutedAmount)
      },
    })
  },
}

export const ORDER_FILLED_AVG_PRICE: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? TradeBlotterHeaders.FILLED_AVG_PRICE,
      field: 'filled_avg_price.pop',
      cellClass: 'tabular-nums flex justify-end',
      valueFormatter: (params: ValueFormatterParams): string => {
        if (!params.value) {
          return PLACEHOLDER
        }
        return getPriceDisplay({
          price: params.value,
          amountType: toOrderSizeType(params.data?.amount?.type),
        })
      },
      minWidth: 125,
      hide: props.hide,
      colId: TradeBlotterColumnId.FILLED_AVG_PRICE,
      width: 100,
      filter: 'agNumberColumnFilter',
    } satisfies ColumnDefinition
  },
}

// Renders an NBBO price cell. `useZeroPlaceholder` gives '—' for an unhydrated
// (null) or zero (no-quote) price. The Last column's publish time lives in its
// own LAST_PRICE_ASOF column rather than inline here.
function formatNbboPriceCell(params: ValueFormatterParams): string {
  return formatMoney(params.value, {
    numDigits: 2,
    addTrailingZeros: true,
    useZeroPlaceholder: true,
  })
}

// Live equity NBBO columns (Last / Bid / Ask), hydrated by useHydrateNbboData.
// Values live on the row so AG Grid sorts natively on the numeric price.
function createNbboPriceColumn(config: {
  field: 'nbboLastPrice' | 'nbboBidPrice' | 'nbboAskPrice'
  headerName: TradeBlotterHeaders
  colId: TradeDeskTradeBlotterColumnId
  hide?: boolean
  width?: number
}): ColumnDefinition {
  return {
    headerName: config.headerName,
    field: config.field,
    cellClass: 'tabular-nums flex justify-end',
    valueFormatter: formatNbboPriceCell,
    minWidth: 50,
    width: config.width ?? 84,
    hide: config.hide,
    colId: config.colId,
    filter: 'agNumberColumnFilter',
  } satisfies ColumnDefinition
}

export const ORDER_NBBO_LAST: ColumnCreator = {
  create: (props: ColumnCreationProps) =>
    createNbboPriceColumn({
      field: 'nbboLastPrice',
      headerName: TradeBlotterHeaders.LAST_PRICE,
      colId: TradeDeskTradeBlotterColumnId.LAST_PRICE,
      hide: props.hide,
    }),
}

// An NBBO quote's publish time as its own sortable column, so traders can sort
// to spot tickers whose quotes have gone stale. The value is an ISO-8601 string,
// so AG Grid sorts it chronologically natively — same as the Time column on
// `created_at`; no comparator needed.
function createNbboAsOfColumn(config: {
  field: 'nbboLastAsOf' | 'nbboBidAsOf' | 'nbboAskAsOf'
  headerName: TradeBlotterHeaders
  colId: TradeDeskTradeBlotterColumnId
  hide?: boolean
}): ColumnDefinition {
  return {
    headerName: config.headerName,
    field: config.field,
    cellClass: 'tabular-nums flex justify-end',
    valueFormatter: (params: ValueFormatterParams): string => {
      if (isNullish(params.value)) {
        return PLACEHOLDER
      }
      return formatDate(parseISOString(params.value), 'M/dd h:mma')
    },
    minWidth: 50,
    width: 120,
    hide: config.hide,
    colId: config.colId,
  } satisfies ColumnDefinition
}

export const ORDER_NBBO_LAST_ASOF: ColumnCreator = {
  create: (props: ColumnCreationProps) =>
    createNbboAsOfColumn({
      field: 'nbboLastAsOf',
      headerName: TradeBlotterHeaders.LAST_PRICE_ASOF,
      colId: TradeDeskTradeBlotterColumnId.LAST_PRICE_ASOF,
      hide: props.hide,
    }),
}

export const ORDER_NBBO_BID: ColumnCreator = {
  create: (props: ColumnCreationProps) =>
    createNbboPriceColumn({
      field: 'nbboBidPrice',
      headerName: TradeBlotterHeaders.BID_PRICE,
      colId: TradeDeskTradeBlotterColumnId.BID_PRICE,
      hide: props.hide,
    }),
}

export const ORDER_NBBO_ASK: ColumnCreator = {
  create: (props: ColumnCreationProps) =>
    createNbboPriceColumn({
      field: 'nbboAskPrice',
      headerName: TradeBlotterHeaders.ASK_PRICE,
      colId: TradeDeskTradeBlotterColumnId.ASK_PRICE,
      hide: props.hide,
    }),
}

export const ORDER_NBBO_BID_ASOF: ColumnCreator = {
  create: (props: ColumnCreationProps) =>
    createNbboAsOfColumn({
      field: 'nbboBidAsOf',
      headerName: TradeBlotterHeaders.BID_PRICE_ASOF,
      colId: TradeDeskTradeBlotterColumnId.BID_PRICE_ASOF,
      hide: props.hide,
    }),
}

export const ORDER_NBBO_ASK_ASOF: ColumnCreator = {
  create: (props: ColumnCreationProps) =>
    createNbboAsOfColumn({
      field: 'nbboAskAsOf',
      headerName: TradeBlotterHeaders.ASK_PRICE_ASOF,
      colId: TradeDeskTradeBlotterColumnId.ASK_PRICE_ASOF,
      hide: props.hide,
    }),
}

// The day's trading range as a single low–high column. Anchored on the low so
// AG Grid sorts natively on a number; both bounds are read from the row for the
// display string. Funds report no intraday range, so the cell is a placeholder.
export const ORDER_NBBO_DAY_RANGE: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: TradeBlotterHeaders.DAY_RANGE,
      field: 'nbboDayLow',
      cellClass: 'tabular-nums flex justify-end',
      valueFormatter: (
        params: ValueFormatterParams<DecoratedAggregatedOrderView>,
      ): string => {
        const low = params.data?.nbboDayLow ?? null
        const high = params.data?.nbboDayHigh ?? null
        if (low === null || high === null) {
          return PLACEHOLDER
        }
        const moneyOptions = { numDigits: 2, addTrailingZeros: true }
        const lowText = formatMoney(low, moneyOptions)
        const highText = formatMoney(high, moneyOptions)
        return `${lowText} – ${highText}`
      },
      minWidth: 50,
      width: 140,
      hide: props.hide,
      colId: TradeDeskTradeBlotterColumnId.DAY_RANGE,
      filter: 'agNumberColumnFilter',
    } satisfies ColumnDefinition
  },
}

// % change from the prior EOD close, already in percent units from the feed
// (e.g. -1.37 → "-1.37%"). Toned green when up / red when down via the shared
// gain/loss helper (no tone at ~zero or when unhydrated). Sorts natively on the
// numeric value.
export const ORDER_NBBO_PERCENT_CHANGE_1D: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: TradeBlotterHeaders.PERCENT_CHANGE_1D,
      field: 'nbboPercentChange1d',
      // getGainLossClass's near-zero epsilon (0.005) reads as 0.005% here, which
      // is genuinely flat — so a sub-rounding move stays untoned, matching the
      // "0.00%" the formatter renders for it.
      cellClass: (
        params: CellClassParams<DecoratedAggregatedOrderView>,
      ): string => {
        const tone = getGainLossClass(params.data?.nbboPercentChange1d ?? null)
        return ['tabular-nums', 'flex', 'justify-end', tone]
          .filter(Boolean)
          .join(' ')
      },
      valueFormatter: (
        params: ValueFormatterParams<DecoratedAggregatedOrderView>,
      ): string => {
        const value = params.data?.nbboPercentChange1d ?? null
        if (value === null) {
          return PLACEHOLDER
        }
        // Sign tracks the rounded display, so a sub-rounding move renders a flat
        // "0.00%" instead of a signed "+0.00%" / "-0.00%".
        const rounded = Number(value.toFixed(2))
        if (rounded === 0) {
          return '0.00%'
        }
        const formatted = formatPercentage(value, { numDigits: 2 })
        return rounded > 0 ? `+${formatted}` : formatted
      },
      minWidth: 50,
      width: 96,
      hide: props.hide,
      colId: TradeDeskTradeBlotterColumnId.PERCENT_CHANGE_1D,
      filter: 'agNumberColumnFilter',
    } satisfies ColumnDefinition
  },
}

export const ORDER_VENUE: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    const getCellValue = (
      params:
        | ICellRendererParams
        | GetQuickFilterTextParams
        | ValueGetterParams,
    ): string => {
      if (isNullish(params.data?.trading_events)) {
        return PLACEHOLDER
      }
      const orderUpdateEvents = (
        params.data.trading_events as TradingEventMessage[]
      )
        .filter(
          (event) => event.msg_type === TradingEventMessageType.OrderUpdate,
        )
        .map((event) => event.event) as OrderUpdateEvent[]
      const executionEvents = orderUpdateEvents.filter(
        (event) => event.event_type === OrderUpdateEventType.Execution,
      ) as OrderExecutionEvent[]
      const venues = executionEvents.map((event) => event.venue as MPID)
      return venues.map((venue) => getVenueLabel(venue)).join(', ')
    }
    return {
      headerName: props.headerName ?? TradeBlotterHeaders.VENUES,
      valueGetter: (params: ValueGetterParams): string => {
        return getCellValue(params)
      },
      getQuickFilterText: (params: GetQuickFilterTextParams): string => {
        return getCellValue(params)
      },
      filterValueGetter: (params: ValueGetterParams): string => {
        return getCellValue(params)
      },
      minWidth: 100,
      hide: props.hide,
      colId: TradeBlotterColumnId.VENUE,
      width: 100,
      filter: 'agSetColumnFilter',
    } satisfies ColumnDefinition
  },
}

export const ORDER_COUNTERPARTY: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    const getCellValue = (
      params:
        | ICellRendererParams
        | GetQuickFilterTextParams
        | ValueGetterParams,
    ): string => {
      if (isNullish(params.data?.trading_events)) {
        return PLACEHOLDER
      }
      const orderUpdateEvents = (
        params.data.trading_events as TradingEventMessage[]
      )
        .filter(
          (event) => event.msg_type === TradingEventMessageType.OrderUpdate,
        )
        .map((event) => event.event) as OrderUpdateEvent[]
      const executionEvents = orderUpdateEvents.filter(
        (event) => event.event_type === OrderUpdateEventType.Execution,
      ) as OrderExecutionEvent[]
      // Exchange-traded equities have no counterparty, so each fill's value is
      // empty; dropping them avoids rendering an order's fills as ", , ,".
      const counterparties = executionEvents
        .map((event) => event.counterparty as string)
        .filter((counterparty) => counterparty)
      if (counterparties.length === 0) {
        return PLACEHOLDER
      }
      return counterparties.join(', ')
    }
    return {
      headerName: props.headerName ?? TradeBlotterHeaders.COUNTERPARTIES,
      valueGetter: (params: ValueGetterParams): string => {
        return getCellValue(params)
      },
      getQuickFilterText: (params: GetQuickFilterTextParams): string => {
        return getCellValue(params)
      },
      filterValueGetter: (params: ValueGetterParams): string => {
        return getCellValue(params)
      },
      minWidth: 130,
      hide: props.hide,
      colId: TradeBlotterColumnId.COUNTERPARTY,
      width: 100,
      filter: 'agSetColumnFilter',
    } satisfies ColumnDefinition
  },
}

export const ORDER_IB_NAME: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? TradeBlotterHeaders.ORGANIZATION,
      field: 'ib_name',
      valueFormatter: (params: ValueFormatterParams): string => {
        if (isNullish(params.value)) {
          return PLACEHOLDER
        }
        return params.value as string
      },
      minWidth: 150,
      hide: props.hide,
      colId: TradeDeskTradeBlotterColumnId.IB_NAME,
      width: 100,
      filter: 'agSetColumnFilter',
    } satisfies ColumnDefinition
  },
}

// Grid `context` shape the blotter supplies so cell renderers can resolve
// related rows from the in-memory order map.
interface AnnotationCellGridContext {
  ordersViewMap?: Map<string, DecoratedAggregatedOrderView>
}

// A GPO parent carries no annotation of its own (grouping has no annotation
// entry), so the annotation column borrows its first child order's annotation.
// The child orders live in the in-memory ordersViewMap, exposed to cell
// renderers via the grid `context`; rows without a context map are unaffected.
function resolveRowAnnotation(
  params: ICellRendererParams<DecoratedAggregatedOrderView>,
): string | null {
  const ownAnnotation = params.data?.annotation
  if (ownAnnotation) {
    return ownAnnotation
  }
  if (!params.data?.is_group_order) {
    return null
  }
  const firstChildId = params.data.group_members?.[0]?.moment_order_id
  if (!firstChildId) {
    return null
  }
  const context = params.context as AnnotationCellGridContext | undefined
  return context?.ordersViewMap?.get(firstChildId)?.annotation ?? null
}

export const ORDER_ICON_ANNOTATION: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? TradeBlotterHeaders.ANNOTATION,
      field: 'annotation',
      wrapHeaderText: false,
      cellRenderer: (params: ICellRendererParams) => {
        const annotation = resolveRowAnnotation(params)
        if (!annotation) {
          return undefined
        }
        return (
          <div className="flex items-center justify-center z-10">
            <Tooltip simpleText={annotation} side="left">
              {/* This is needed because the icon is an SVG that does not support pointer-events */}
              <div className="pointer-events-auto">
                <Icon
                  iconName={IconName.ChatBubbleBottomCenterTextIcon}
                  outline
                  className="h-4 w-4"
                />
              </div>
            </Tooltip>
          </div>
        )
      },
      // Equities UI rollout: render the annotation text in the cell instead of
      // the icon+tooltip. Sizing/resizability is handled uniformly by the
      // rollout layout (every column becomes resizable with a 50px floor).
      proposedCellRenderer: (params: ICellRendererParams) => {
        const annotation = resolveRowAnnotation(params)
        if (!annotation) {
          return undefined
        }
        return annotation
      },
      minWidth: 50,
      maxWidth: 50,
      resizable: false,
      sortable: false,
      filter: false,
      hide: props.hide,
      colId: TradeBlotterColumnId.ICON_ANNOTATION,
      width: 108,
    } satisfies TradeBlotterColumnDefinition
  },
}

export const ORDER_PROGRESS: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? TradeBlotterHeaders.FILL_PROGRESS,
      hide: props.hide,
      minWidth: 150,
      maxWidth: 150,
      resizable: false,
      colId: TradeDeskTradeBlotterColumnId.ORDER_PROGRESS,
      width: 99,
      // The cell renders a progress bar (no serializable value), so getDataAsCsv
      // has nothing to export. Expose the fill percentage as a number for the CSV
      // and sorting; the cellRenderer still drives the on-screen display.
      valueGetter: (params: ValueGetterParams): number | null => {
        const filled = params.data?.filled_amount?.value
        const total = params.data?.amount?.value
        if (
          typeof filled !== 'number' ||
          typeof total !== 'number' ||
          total === 0
        ) {
          return null
        }
        return (filled / total) * 100
      },
      valueFormatter: (params: ValueFormatterParams): string => {
        if (params.value == null) {
          return ''
        }
        return formatPercentage(params.value, { numDigits: 0 })
      },
      cellRenderer: (params: ICellRendererParams) => {
        const fillProgress =
          (params.data.filled_amount.value / params.data.amount.value) * 100

        return (
          <div className="flex flex-row w-full gap-2 items-center">
            <ProgressBar value={fillProgress} />
            <BodySubtext>
              {formatPercentage(fillProgress, { numDigits: 0 })}
            </BodySubtext>
          </div>
        )
      },
    } satisfies ColumnDefinition
  },
}

export const ORDER_GPO_STATUS: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: TradeBlotterHeaders.GROUP,
      hide: props.hide,
      minWidth: 60,
      maxWidth: 60,
      resizable: false,
      colId: TradeDeskTradeBlotterColumnId.GPO_STATUS,
      width: 55,
      rolloutMinWidth: 40,
      sortable: false,
      // The cell renders an icon (no serializable value), so getDataAsCsv has
      // nothing to export. Expose a plain string for the CSV; the cellRenderer
      // still drives the on-screen display.
      valueGetter: (params: ValueGetterParams): string =>
        params.data?.is_group_order ? 'Yes' : '',
      cellRenderer: (params: ICellRendererParams) => {
        if (!params.data.is_group_order) {
          return null
        }

        return (
          <div className="flex w-full gap-2 items-center justify-start">
            <Icon
              iconName={IconName.RectangleStackIcon}
              size="medium"
              className="fill-moment-blue-grey-800"
            />
            {params.data?.group_members && (
              <BodySubtext>{params.data.group_members.length}</BodySubtext>
            )}
          </div>
        )
      },
    } satisfies TradeBlotterColumnDefinition
  },
}

// Under `trading_launch.showTraderInitialsOnBlotter`, the Trader column
// renders initials instead of the full name to save horizontal space. Falls
// back to the untouched value when it isn't a "First Last" name (e.g. an
// email or raw user ID) — abbreviating those would produce meaningless
// letters rather than real initials.
function getInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length < 2) {
    return name
  }
  const firstWord = words[0] ?? ''
  const lastWord = words[words.length - 1] ?? ''
  return `${firstWord[0] ?? ''}${lastWord[0] ?? ''}`.toUpperCase()
}

export const ORDER_TRADER_ID: ColumnCreator = {
  // We're passing the user data in this props argument
  create: (props: ColumnCreationProps) => {
    // We're matching the user_id in the order metadata with the map of users passed in the props argument.
    const users = props?.meta?.users as
      | Record<string, Partial<UserBase>>
      | undefined
    const showTraderInitials = props?.meta?.showTraderInitials
    const getCellValue = (
      params:
        | ICellRendererParams
        | GetQuickFilterTextParams
        | ValueGetterParams,
    ): string => {
      return getHumanReadableTraderIdentifier(params.data, users)
    }
    return {
      headerName: props.headerName ?? TradeBlotterHeaders.TRADER,
      minWidth: 120,
      colId: TradeDeskTradeBlotterColumnId.TRADER_ID,
      width: 57,
      valueGetter: (params: ValueGetterParams): string => {
        return getCellValue(params)
      },
      getQuickFilterText: (params: GetQuickFilterTextParams): string => {
        return getCellValue(params)
      },
      filterValueGetter: (params: ValueGetterParams): string => {
        return (
          params.data?.metadata?.[OrderMetadataFields.TraderId] ??
          UNASSIGNED_TRADER_ID
        )
      },
      filter: 'agSetColumnFilter',
      filterParams: {
        valueFormatter: (params: ValueFormatterParams): string => {
          if (
            !params.value ||
            params.value === UNASSIGNED_TRADER_ID ||
            !users
          ) {
            return params.value
          }
          return getHumanReadableUserIdentifierById(params.value, users)
        },
      },
      cellRenderer: showTraderInitials
        ? (params: ICellRendererParams) => {
            const fullName = getCellValue(params)
            return <span title={fullName}>{getInitials(fullName)}</span>
          }
        : undefined,
    } satisfies ColumnDefinition
  },
}

export const ORDER_SUBFIRM: ColumnCreator = {
  create: () => {
    const getSubfirm = (
      params:
        | ICellRendererParams
        | GetQuickFilterTextParams
        | ValueGetterParams,
    ): string => {
      return params.data?.metadata?.[OrderMetadataFields.Subfirm] ?? PLACEHOLDER
    }
    return {
      headerName: TradeBlotterHeaders.SUBFIRM,
      minWidth: 90,
      colId: TradeDeskTradeBlotterColumnId.SUBFIRM,
      width: 90,
      valueGetter: getSubfirm,
      getQuickFilterText: getSubfirm,
      filterValueGetter: getSubfirm,
      filter: 'agSetColumnFilter',
    } satisfies ColumnDefinition
  },
}

export const ORDER_PLATFORM: ColumnCreator = {
  create: () => {
    const getPlatform = (
      params:
        | ICellRendererParams
        | GetQuickFilterTextParams
        | ValueGetterParams,
    ): string => {
      const platform = params.data?.metadata?.[
        OrderMetadataFields.Platform
      ] as LPLPlatform

      return LPLPlatformLabels?.[platform as LPLPlatform] ?? PLACEHOLDER
    }
    return {
      headerName: TradeBlotterHeaders.PLATFORM,
      minWidth: 120,
      colId: TradeDeskTradeBlotterColumnId.PLATFORM,
      width: 100,
      valueGetter: getPlatform,
      getQuickFilterText: getPlatform,
      filterValueGetter: getPlatform,
      filter: 'agSetColumnFilter',
    } satisfies ColumnDefinition
  },
}

export const ORDER_HAS_DISCRETIONARY_AUTHORITY: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    const getCellValue = (
      params:
        | ICellRendererParams
        | GetQuickFilterTextParams
        | ValueGetterParams,
    ): string => {
      if (!params.data) {
        return PLACEHOLDER
      }
      return (
        formatDiscretionaryAuthority(
          resolveOrderDiscretionaryAuthority(params.data),
        ) ?? PLACEHOLDER
      )
    }
    return {
      headerName:
        props.headerName ?? TradeBlotterHeaders.DISCRETIONARY_AUTHORITY,
      minWidth: 150,
      colId: TradeDeskTradeBlotterColumnId.HAS_DISCRETIONARY_AUTHORITY,
      width: 100,
      valueGetter: (params: ValueGetterParams): string => {
        return getCellValue(params)
      },
      getQuickFilterText: (params: GetQuickFilterTextParams): string => {
        return getCellValue(params)
      },
      filterValueGetter: (params: ValueGetterParams): string => {
        return getCellValue(params)
      },
      // Important for Aggrid to work properly.
      filter: 'agSetColumnFilter',
    } satisfies ColumnDefinition
  },
}

export const ORDER_ADVISOR_NAME: ColumnCreator = {
  // We're passing the user data in this props argument
  create: (props: ColumnCreationProps) => {
    // We're matching the user_id in the order metadata with the map of users passed in the props argument.
    const getCellValue = (
      params:
        | ICellRendererParams
        | GetQuickFilterTextParams
        | ValueGetterParams,
    ): string => {
      return (
        params.data?.metadata?.[OrderMetadataFields.AdvisorName] ?? PLACEHOLDER
      )
    }
    return {
      headerName: props.headerName ?? TradeBlotterHeaders.IAR,
      minWidth: 120,
      colId: TradeDeskTradeBlotterColumnId.ADVISOR_NAME,
      width: 100,
      valueGetter: (params: ValueGetterParams): string => {
        return getCellValue(params)
      },
      getQuickFilterText: (params: GetQuickFilterTextParams): string => {
        return getCellValue(params)
      },
      filterValueGetter: (params: ValueGetterParams): string => {
        return getCellValue(params)
      },
      // Important for Aggrid to work properly.
      filter: 'agSetColumnFilter',
    } satisfies ColumnDefinition
  },
}

export const ORDER_ADVISOR_EMAIL: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    const getCellValue = (
      params:
        | ICellRendererParams
        | GetQuickFilterTextParams
        | ValueGetterParams,
    ): string => {
      return (
        params.data?.metadata?.[OrderMetadataFields.AdvisorEmail] ?? PLACEHOLDER
      )
    }
    return {
      headerName: props.headerName ?? TradeBlotterHeaders.IAR_EMAIL,
      field: OrderMetadataFields.AdvisorEmail, // This field is set on order metadata - keep this in sync with the field in the order entry form
      hide: props.hide,
      minWidth: 120,
      colId: TradeDeskTradeBlotterColumnId.ADVISOR_EMAIL,
      width: 100,
      valueGetter: (params: ValueGetterParams): string => {
        return getCellValue(params)
      },
      getQuickFilterText: (params: GetQuickFilterTextParams): string => {
        return getCellValue(params)
      },
      filterValueGetter: (params: ValueGetterParams): string => {
        return getCellValue(params)
      },
      // Important for Aggrid to work properly.
      filter: 'agSetColumnFilter',
    } satisfies ColumnDefinition
  },
}

export const ORDER_SUBMISSION_NAME: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    const getCellValue = (
      params:
        | ICellRendererParams
        | GetQuickFilterTextParams
        | ValueGetterParams,
    ): string => {
      return (
        params.data?.metadata?.[OrderMetadataFields.SubmissionName] ??
        PLACEHOLDER
      )
    }
    return {
      headerName: props.headerName ?? TradeBlotterHeaders.SUBMISSION_NAME,
      field: OrderMetadataFields.SubmissionName,
      hide: props.hide,
      minWidth: 200,
      colId: TradeDeskTradeBlotterColumnId.SUBMISSION_NAME,
      width: 100,
      valueGetter: (params: ValueGetterParams): string => {
        return getCellValue(params)
      },
      getQuickFilterText: (params: GetQuickFilterTextParams): string => {
        return getCellValue(params)
      },
      filterValueGetter: (params: ValueGetterParams): string => {
        return getCellValue(params)
      },
      filter: 'agSetColumnFilter',
    } satisfies ColumnDefinition
  },
}

export const ORDER_ACCRUED_INTEREST: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? TradeBlotterHeaders.ACCRUED_INTEREST,
      field: 'accrued_interest',
      cellClass: 'tabular-nums flex justify-end',
      valueFormatter: (params: ValueFormatterParams): string => {
        if (!isFixedIncomeAmount(params.data?.amount)) {
          return PLACEHOLDER
        }
        if (isNullish(params.value)) {
          return PLACEHOLDER
        }
        return formatMoney(params.value)
      },
      minWidth: 125,
      hide: props.hide,
      colId: TradeDeskTradeBlotterColumnId.ACCRUED_INTEREST,
      width: 84,
      filter: 'agNumberColumnFilter',
    } satisfies ColumnDefinition
  },
}

/**
 * Decides what the Filled YTW cell renders: PLACEHOLDER for non-FI rows
 * (per FR-043), PLACEHOLDER for FI rows with null yield, otherwise the
 * formatted yield. Extracted from `ORDER_YTW.valueFormatter` so tests can
 * exercise the decision logic directly without constructing ag-grid params.
 */
export function formatYtwCell(
  value: number | null,
  assetClass: AssetClass | null,
): string {
  if (!isFixedIncome(assetClass)) return PLACEHOLDER
  if (isNullish(value)) return PLACEHOLDER
  return formatYield(value)
}

export const ORDER_YTW: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? 'Filled YTW',
      headerClass: props.headerClass ?? 'ag-right-aligned-header',
      colId: props.colId ?? TradeBlotterColumnId.YTW,
      width: 109,
      field: 'filled_avg_price.ytw',
      filter: props.filter ?? 'agNumberColumnFilter',
      cellClass: 'ag-right-aligned-cell tabular-nums w-full flex justify-end',
      valueFormatter: (params: ValueFormatterParams): string =>
        formatYtwCell(
          params.value,
          params.data ? getAssetClassFromOrder(params.data) : null,
        ),
      cellRenderer: (params: ICellRendererParams) => params.valueFormatted,
      minWidth: props.minWidth ?? 150,
      hide: props.hide,
    } satisfies ColumnDefinition
  },
}

export const ORDER_SETTLEMENT_DATE: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? TradeBlotterHeaders.SETTLEMENT_DATE,
      field: 'settlement_date',
      cellClass: 'tabular-nums',
      valueFormatter: (params: ValueFormatterParams): string => {
        if (isNullish(params.value)) {
          return PLACEHOLDER
        }
        return formatIntegerToUSDate(params.value) ?? PLACEHOLDER
      },
      minWidth: 120,
      hide: props.hide,
      colId: TradeDeskTradeBlotterColumnId.SETTLEMENT_DATE,
      width: 100,
      filter: 'agSetColumnFilter',
    } satisfies ColumnDefinition
  },
}

export const ORDER_NET_MONEY: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? TradeBlotterHeaders.NET_MONEY,
      field: 'filled_net_money',
      cellClass: 'tabular-nums flex justify-end',
      valueFormatter: (params: ValueFormatterParams): string => {
        // Mutual fund orders' net money is the same number as their notional
        // amount; render PLACEHOLDER to avoid duplicating it across columns.
        if (isMutualFundAmount(params.data?.amount)) {
          return PLACEHOLDER
        }
        if (isNullish(params.value)) {
          return PLACEHOLDER
        }
        return formatMoney(params.value)
      },
      minWidth: 125,
      hide: props.hide,
      colId: TradeDeskTradeBlotterColumnId.NET_MONEY,
      width: 100,
      filter: 'agNumberColumnFilter',
    } satisfies ColumnDefinition
  },
}

export const ORDER_PRE_FEE_NET_MONEY: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    return {
      headerName: props.headerName ?? TradeBlotterHeaders.PRE_FEE_NET_MONEY,
      field: 'filled_pre_fee_net_money',
      cellClass: 'tabular-nums flex justify-end',
      valueFormatter: (params: ValueFormatterParams): string => {
        if (isMutualFundAmount(params.data?.amount)) {
          return PLACEHOLDER
        }
        if (isNullish(params.value)) {
          return PLACEHOLDER
        }
        return formatMoney(params.value)
      },
      minWidth: 150,
      hide: props.hide,
      colId: TradeDeskTradeBlotterColumnId.PRE_FEE_NET_MONEY,
      width: 100,
      filter: 'agNumberColumnFilter',
    } satisfies ColumnDefinition
  },
}

export const ORDER_LIST_RFQ_NAME: ColumnCreator = {
  create: (props: ColumnCreationProps) => {
    const getCellValue = (
      params:
        | ICellRendererParams
        | GetQuickFilterTextParams
        | ValueGetterParams,
    ): string => {
      const rfqViews = params.data?.rfq_views ?? []
      const rfqWithListName = rfqViews.find(
        (rfq: AggregatedRfqView) => rfq.latest_state.list_name,
      )
      return rfqWithListName?.latest_state.list_name ?? PLACEHOLDER
    }
    return {
      headerName: props.headerName ?? TradeBlotterHeaders.LIST_RFQ_NAME,
      field: 'rfq_views',
      hide: props.hide,
      minWidth: 200,
      colId: TradeDeskTradeBlotterColumnId.LIST_RFQ_NAME,
      width: 100,
      valueGetter: (params: ValueGetterParams): string => {
        return getCellValue(params)
      },
      getQuickFilterText: (params: GetQuickFilterTextParams): string => {
        return getCellValue(params)
      },
      filterValueGetter: (params: ValueGetterParams): string => {
        return getCellValue(params)
      },
      filter: 'agSetColumnFilter',
    } satisfies ColumnDefinition
  },
}
